import api from './api';
import { createSSEParser } from './chatStream';

// Machine-readable blocks the experts append for the code; viewers never see them.
const HIDDEN_MARKERS = ['STANCE_JSON_START', 'POSITIONS_JSON_START', 'AGENDA_JSON_START'];

export const visibleText = (text) => {
  let cut = text.length;
  HIDDEN_MARKERS.forEach((m) => {
    const i = text.indexOf(m);
    if (i >= 0 && i < cut) cut = i;
  });
  return text.slice(0, cut).replace(/```(json)?\s*$/, '').trimEnd();
};

export const emptyLiveState = { run: null, entries: [], streams: {}, finished: false };

const sameText = (stream, entry) => stream.phase === entry.phase
  && stream.expert_name === entry.expert_name
  && (stream.round || 0) === (entry.round || 0);

// applyLiveEvent folds one SSE event of GET /analysis/expert-reports/:id/live
// into the viewer state. A stream disappears once its stored entry arrives.
export function applyLiveEvent(state, name, payload) {
  switch (name) {
    case 'state':
      return { ...state, run: { ...state.run, ...payload } };
    case 'entry': {
      if (state.entries.some((e) => e.id === payload.id)) return state;
      const streams = Object.fromEntries(Object.entries(state.streams).filter(([, s]) => !sameText(s, payload)));
      return { ...state, entries: [...state.entries, payload], streams };
    }
    case 'stream': {
      // After a reconnection the server replays everything: a text already
      // stored as an entry must not come back as a live bubble.
      if (state.entries.some((e) => sameText(payload, e))) return state;
      const prev = state.streams[payload.key];
      const { delta, offset, ...meta } = payload;
      const text = offset === 0 || !prev ? delta : prev.text + delta;
      return { ...state, streams: { ...state.streams, [payload.key]: { ...prev, ...meta, text, done: false } } };
    }
    case 'stream_end': {
      const prev = state.streams[payload.key];
      return prev ? { ...state, streams: { ...state.streams, [payload.key]: { ...prev, done: true } } } : state;
    }
    case 'done':
      return { ...state, run: { ...state.run, ...payload }, streams: {}, finished: true };
    default:
      return state;
  }
}

// followExpertRun streams the live events of a run until the server closes the
// stream (run finished), the signal aborts, or an error occurs.
export async function followExpertRun({ runId, onEvent, signal }) {
  if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEMO === 'true') {
    return require('../demo/streams').followRunDemo({ runId, onEvent, signal });
  }
  const token = localStorage.getItem('token');
  const res = await fetch(`${api.defaults.baseURL}/analysis/expert-reports/${encodeURIComponent(runId)}/live`, {
    headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    signal,
  });
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || 'Live debate unavailable');
  }
  const push = createSSEParser((event, data) => {
    let payload;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    onEvent(event, payload);
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    push(decoder.decode(value, { stream: true }));
  }
  push(decoder.decode());
}
